import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"

import CompetitorProductsFilterChips from "../components/CompetitorProductsFilterChips"
import { CompetitorProductsFilterDrawer } from "../components/CompetitorProductsFilterDrawer"
import CompetitorProductFormDialog from "../dialogs/CompetitorProductFormDialog"
import { useCompetitorProductActions } from "../hooks/useCompetitorProductActions"
import { useCompetitorProductPermissions } from "../hooks/useCompetitorProductPermissions"
import useCompetitorProductSearchList from "../hooks/useCompetitorProductSearchList"
import CompetitorProductsTable from "../tables/CompetitorProductsTable"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { CompetitorProduct, CompetitorProductSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const CompetitorProductsContainer: FC = () => {
  const navigate = useNavigate()
  const [isFormDialogOpen, setIsFormDialogOpen] = useState<boolean>(false)
  const [editingProduct, setEditingProduct] = useState<CompetitorProduct | undefined>(undefined)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)

  const { hasFullAccess } = useCompetitorProductPermissions()
  const { data, fetching, error, searchParams, refetchData, clearStore } = useCompetitorProductSearchList()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  // Load data with filters from URL
  useEffect(() => {
    refetchData(false)
  }, [refetchData])

  const toggleFilterDrawer = () => setShowFilterDrawer((prev) => !prev)

  const { deleteCompetitorProductData } = useCompetitorProductActions({
    onSuccess: () => refetchData(),
  })

  const sorting = useMemo(() => {
    if (!searchParams.field) return []

    const tableSortField = upperSnakeCaseToCamelCase(searchParams.field)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: searchParams.order === SortingOrder.Desc,
      },
    ]
  }, [searchParams.field, searchParams.order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length) {
      const sort = newSorting[0]
      const key = sort.id
      const field = toUpperSnakeCase(key) as CompetitorProductSortField
      const params = {
        query: searchParams.query,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        distributorIds: searchParams.distributorIds,
      }

      navigate({
        search: params,
        to: "/app/admin/manage-products/competitor-products",
        replace: true,
      })
    }
  }

  const handleOpenFormDialog = (product?: CompetitorProduct) => {
    setEditingProduct(product)
    setIsFormDialogOpen(true)
  }

  const handleCloseFormDialog = () => {
    setEditingProduct(undefined)
    setIsFormDialogOpen(false)
  }

  const handleDelete = async (competitorProductId: string, productName: string): Promise<boolean> => {
    return await deleteCompetitorProductData(competitorProductId, productName)
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <TableHeaderActions currentSearch={searchParams} placeholder='Search by CAS#, Product Name or Chemical Name'>
        <Button
          appearance='outlined'
          onClick={toggleFilterDrawer}
          startIcon={<FilterAltOutlined />}
          data-testid='filter-button'
        >
          Filter
        </Button>
        {hasFullAccess && (
          <Button
            appearance='outlined'
            startIcon={<AddOutlined />}
            onClick={() => handleOpenFormDialog()}
            data-testid='new-competitor-product-button'
          >
            New Product
          </Button>
        )}
      </TableHeaderActions>

      <CompetitorProductsFilterChips />

      <Box className='grow overflow-hidden'>
        <CompetitorProductsTable
          data={data}
          fetching={fetching}
          error={error}
          sorting={sorting}
          onHandleSortingChange={handleSortingChange}
          onEdit={handleOpenFormDialog}
          onDelete={handleDelete}
        />
      </Box>

      <CompetitorProductsFilterDrawer isOpen={showFilterDrawer} onToggle={toggleFilterDrawer} />

      {isFormDialogOpen && (
        <CompetitorProductFormDialog
          data={editingProduct}
          onSuccess={() => refetchData()}
          onClose={handleCloseFormDialog}
        />
      )}
    </Box>
  )
}

export default CompetitorProductsContainer
